import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { transcriptsAPI } from '../services/api';

// Helper to get media URL with auth token
export const getAuthenticatedMediaUrl = (mediaUrl) => {
  if (!mediaUrl) return null;
  const token = localStorage.getItem('token');
  const streamUrl = mediaUrl.replace('/api/files/', '/api/files/stream/');
  return `${streamUrl}?token=${encodeURIComponent(token)}`;
};

export function formatTime(ms) {
  const seconds = Math.floor(ms / 1000);
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// Index of the last item whose start is <= ms, or -1 if ms is before the first.
// (Matches the old findIndex: item i is active while ms is in [start_i, start_i+1).)
export function lastIndexStartingAtOrBefore(items, ms) {
  let lo = 0;
  let hi = items.length - 1;
  let result = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (items[mid].start <= ms) {
      result = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return result;
}

export default function useTranscriptViewer(id) {
  const [transcript, setTranscript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const mediaPlayerRef = useRef(null);
  const transcriptContainerRef = useRef(null);

  useEffect(() => {
    const loadTranscript = async () => {
      try {
        const response = await transcriptsAPI.get(id);
        setTranscript(response.data);
      } catch (err) {
        setError('Failed to load transcript');
      } finally {
        setLoading(false);
      }
    };

    loadTranscript();
  }, [id]);

  // Active utterance for the current time (derived; binary search since
  // utterances are sorted by start)
  const activeUtteranceIndex = useMemo(() => {
    const utterances = transcript?.utterances;
    if (!utterances?.length) return -1;
    return lastIndexStartingAtOrBefore(utterances, currentTime * 1000);
  }, [currentTime, transcript]);

  const handleTimeUpdate = useCallback((time) => {
    setCurrentTime(time);
  }, []);

  const handleLineClick = useCallback((startMs) => {
    if (mediaPlayerRef.current) {
      mediaPlayerRef.current.seekTo(startMs / 1000);
    }
  }, []);

  const handleDownload = async (format) => {
    try {
      const response = await transcriptsAPI.download(id, format);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = transcript.original_filename.replace(/\.[^/.]+$/, `.${format}`);
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleDownloadMedia = () => {
    const token = localStorage.getItem('token');
    const filename = transcript.media_url.replace('/api/files/', '');
    const url = `/api/files/stream/${encodeURIComponent(filename)}?token=${encodeURIComponent(token)}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = transcript.original_filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const copyAllText = () => {
    if (!transcript?.utterances) return;

    const text = transcript.utterances
      .map((u) => `[Speaker ${u.speaker}] ${u.text}`)
      .join('\n\n');

    navigator.clipboard.writeText(text);
  };

  const handleWordClick = useCallback((startMs) => {
    if (mediaPlayerRef.current) {
      mediaPlayerRef.current.seekTo(startMs / 1000);
    }
  }, []);

  // Group utterances by speaker for paragraph display
  const groupedUtterances = useMemo(() => {
    if (!transcript?.utterances) return [];

    const groups = [];
    let currentGroup = null;

    transcript.utterances.forEach((u, index) => {
      // Utterances without word timings get pseudo-words at the utterance
      // start (zero length, so they never show as "spoken") so search
      // highlighting and click-to-seek still work.
      const words = u.words?.length
        ? u.words
        : (u.text || '').split(/\s+/).filter(Boolean).map((text) => ({ text, start: u.start, end: u.start }));

      if (!currentGroup || currentGroup.speaker !== u.speaker) {
        if (currentGroup) groups.push(currentGroup);
        currentGroup = {
          speaker: u.speaker,
          start: u.start,
          end: u.end,
          text: u.text,
          words: [...words],
          utterances: [{ ...u, originalIndex: index }],
        };
      } else {
        currentGroup.text += ' ' + u.text;
        currentGroup.end = u.end;
        currentGroup.words.push(...words);
        currentGroup.utterances.push({ ...u, originalIndex: index });
      }
    });

    if (currentGroup) groups.push(currentGroup);
    return groups;
  }, [transcript]);

  return {
    transcript,
    loading,
    error,
    currentTime,
    activeUtteranceIndex,
    mediaPlayerRef,
    transcriptContainerRef,
    groupedUtterances,
    handleTimeUpdate,
    handleLineClick,
    handleDownload,
    handleDownloadMedia,
    copyAllText,
    handleWordClick,
  };
}
